/**
 * Browser side of the live feed: one WebSocket to `/api/ws/live`, one
 * symbol subscription, and a reducer that keeps the latest L1 / depth /
 * per-order book plus a rolling trade tape and the market-wide streams.
 *
 * Wire types mirror apps/api/src/services/liveFeed.ts.
 */
import { useEffect, useReducer, useRef } from 'react';
import { api } from './api';
import type { ApiTick } from './hooks';

const BASE_URL = (import.meta.env.VITE_API_URL ?? '') as string;
const MAX_TRADES = 250;
const MAX_ALERTS = 50;
const MAX_EVENTS = 40;

export interface LiveL1 {
  sym: string;
  market: 'REG' | 'ODL';
  bid: number;
  bidQty: number;
  ask: number;
  askQty: number;
  status: string;
  dir: string;
  change: number;
  time: string;
  tsMs: number;
  last: number;
  flag: string;
  average: number;
  high: number;
  low: number;
  trades: number;
  volume: number;
  lastTradeVolume: number;
  close: number;
  open: number;
  receivedAt: number;
}
export interface DepthLevel {
  orders: number;
  qty: number;
  price: number;
}
export interface LiveMbp {
  sym: string;
  bids: DepthLevel[];
  asks: DepthLevel[];
  receivedAt: number;
}
export interface MboOrder {
  price: number;
  qty: number;
  flag: string;
  orderId: string;
}
export interface LiveMbo {
  sym: string;
  bids: MboOrder[];
  asks: MboOrder[];
  receivedAt: number;
}
export interface LiveTrade {
  sym: string;
  price: number;
  volume: number;
  time: string;
  tsMs: number;
  /** >1 when the feed coalesced several prints into one push. 0 for tape rows. */
  coalesced: number;
  receivedAt: number;
  /** 'live' arrived over the socket; 'tape' came from the HTTP bootstrap. */
  source: 'live' | 'tape';
}
export interface LiveAlert {
  kind: 'ADDUCAP' | 'ADDLCAP' | 'RUCAP' | 'RLCAP';
  sym: string;
  price: number | null;
  raw: string;
  receivedAt: number;
}
export interface LiveIndex {
  name: string;
  value: number;
  change: number;
  high: number;
  highChange: number;
  low: number;
  lowChange: number;
  advancers: number;
  decliners: number;
  unchanged: number;
  volume: number;
  valueTraded: number;
  feedTime: string;
  receivedAt: number;
}
export interface LiveMarketStat {
  market: string;
  status: string;
  volume: number;
  trades: number;
  valueTraded: number;
}
export interface LiveStats {
  shariah: boolean;
  stats: LiveMarketStat[];
  receivedAt: number;
}
export interface LiveMovers {
  shariah: boolean;
  categories: Record<string, string[]>;
  receivedAt: number;
}
export interface FeedStatus {
  enabled: boolean;
  connected: boolean;
  host: string;
  subscribed: string[];
  wanted: string[];
  depth?: string[];
  lastMessageAt: number | null;
  connectedSince: number | null;
  disconnects: number;
  lastDisconnectReason: string | null;
  messageCounts: Record<string, number>;
  updatedAt: number;
}

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface LiveEvent {
  at: number;
  type: string;
  summary: string;
}

export interface LiveState {
  socket: SocketStatus;
  feed: FeedStatus | null;
  l1: LiveL1 | null;
  mbp: LiveMbp | null;
  mbo: LiveMbo | null;
  trades: LiveTrade[];
  alerts: LiveAlert[];
  indices: Record<string, LiveIndex>;
  stats: LiveStats | null;
  shariahStats: LiveStats | null;
  movers: LiveMovers | null;
  shariahMovers: LiveMovers | null;
  /** Count of every message type this client has received. */
  counts: Record<string, number>;
  events: LiveEvent[];
  lastMessageAt: number | null;
  bootstrapped: boolean;
  liveTradeCount: number;
}

const initial = (): LiveState => ({
  socket: 'connecting',
  feed: null,
  l1: null,
  mbp: null,
  mbo: null,
  trades: [],
  alerts: [],
  indices: {},
  stats: null,
  shariahStats: null,
  movers: null,
  shariahMovers: null,
  counts: {},
  events: [],
  lastMessageAt: null,
  bootstrapped: false,
  liveTradeCount: 0,
});

type Action =
  | { kind: 'reset' }
  | { kind: 'socket'; status: SocketStatus }
  | { kind: 'bootstrap'; ticks: ApiTick[]; sym: string }
  | { kind: 'message'; msg: Record<string, unknown> & { type: string }; sym: string };

const tradeKey = (t: { time: string; price: number; volume: number }) =>
  `${t.time}|${t.price}|${t.volume}`;

function pushEvent(events: LiveEvent[], type: string, summary: string): LiveEvent[] {
  return [{ at: Date.now(), type, summary }, ...events].slice(0, MAX_EVENTS);
}

function reduce(state: LiveState, a: Action): LiveState {
  switch (a.kind) {
    case 'reset':
      return initial();
    case 'socket':
      return {
        ...state,
        socket: a.status,
        events: pushEvent(state.events, 'socket', a.status),
      };
    case 'bootstrap': {
      // Tape rows fill the panel instantly; live rows that arrive later are
      // deduped against them by (time, price, volume).
      const seen = new Set(state.trades.map(tradeKey));
      const tape: LiveTrade[] = [];
      for (const t of [...a.ticks].reverse()) {
        const row: LiveTrade = {
          sym: a.sym,
          price: t.price,
          volume: t.volume,
          time: t.time,
          tsMs: t.tsMs,
          coalesced: 0,
          receivedAt: t.tsMs,
          source: 'tape',
        };
        if (seen.has(tradeKey(row))) continue;
        tape.push(row);
      }
      return {
        ...state,
        bootstrapped: true,
        trades: [...state.trades, ...tape].slice(0, MAX_TRADES),
        events: pushEvent(state.events, 'tape', `${tape.length} prints from /ticks`),
      };
    }
    case 'message': {
      const m = a.msg;
      const counts = { ...state.counts, [m.type]: (state.counts[m.type] ?? 0) + 1 };
      const base = { ...state, counts, lastMessageAt: Date.now() };
      switch (m.type) {
        case 'status':
          return { ...base, feed: m as unknown as FeedStatus };
        case 'subscribed':
          return {
            ...base,
            events: pushEvent(state.events, 'subscribed', `${String(m.symbol)} (${String(m.cached)} cached)`),
          };
        case 'l1': {
          const l1 = m as unknown as LiveL1;
          if (l1.sym !== a.sym) return base;
          return { ...base, l1 };
        }
        case 'mbp': {
          const mbp = m as unknown as LiveMbp;
          return mbp.sym === a.sym ? { ...base, mbp } : base;
        }
        case 'mbo': {
          const mbo = m as unknown as LiveMbo;
          return mbo.sym === a.sym ? { ...base, mbo } : base;
        }
        case 'trade': {
          const t = m as unknown as Omit<LiveTrade, 'source'>;
          if (t.sym !== a.sym) return base;
          const row: LiveTrade = { ...t, source: 'live' };
          const key = tradeKey(row);
          // A tape row for the same print may already be present if the
          // bootstrap raced the socket; keep the live one on top.
          const rest = state.trades.filter((x) => tradeKey(x) !== key);
          return {
            ...base,
            trades: [row, ...rest].slice(0, MAX_TRADES),
            liveTradeCount: state.liveTradeCount + 1,
          };
        }
        case 'alert': {
          const al = m as unknown as LiveAlert;
          return {
            ...base,
            alerts: [al, ...state.alerts].slice(0, MAX_ALERTS),
            events: pushEvent(state.events, 'alert', `${al.kind} ${al.sym} ${al.price ?? ''}`),
          };
        }
        case 'index': {
          const ix = m as unknown as LiveIndex;
          return { ...base, indices: { ...state.indices, [ix.name]: ix } };
        }
        case 'stats': {
          const s = m as unknown as LiveStats;
          return s.shariah ? { ...base, shariahStats: s } : { ...base, stats: s };
        }
        case 'movers': {
          const mv = m as unknown as LiveMovers;
          return mv.shariah ? { ...base, shariahMovers: mv } : { ...base, movers: mv };
        }
        case 'error':
          return { ...base, events: pushEvent(state.events, 'error', String(m.message ?? '')) };
        case 'pong':
        case 'unsubscribed':
          return base;
        default:
          return { ...base, events: pushEvent(state.events, 'unknown', m.type) };
      }
    }
  }
}

export function liveSocketUrl(): string {
  const httpBase = BASE_URL || window.location.origin;
  return httpBase.replace(/^http/, 'ws') + '/api/ws/live';
}

/**
 * Subscribe to one symbol's live feed. Reconnects with backoff; re-subscribes
 * after every (re)connect. Switching `sym` resets the state.
 */
export function useLiveFeed(sym: string | null): LiveState {
  const [state, dispatch] = useReducer(reduce, undefined, initial);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    dispatch({ kind: 'reset' });
    if (!sym) return;
    let cancelled = false;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;

    // Bootstrap the tape from the HTTP stream in parallel with the socket.
    void api
      .get<{ ticks: ApiTick[] }>(`/stocks/${sym}/ticks?limit=120`)
      .then((r) => {
        if (!cancelled) dispatch({ kind: 'bootstrap', ticks: r.ticks, sym });
      })
      .catch(() => {
        if (!cancelled) dispatch({ kind: 'bootstrap', ticks: [], sym });
      });

    const connect = () => {
      if (cancelled) return;
      dispatch({ kind: 'socket', status: attempt === 0 ? 'connecting' : 'reconnecting' });
      const ws = new WebSocket(liveSocketUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        dispatch({ kind: 'socket', status: 'open' });
        ws.send(JSON.stringify({ type: 'subscribe', symbol: sym }));
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25_000);
      };
      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(String(ev.data)) as Record<string, unknown> & { type: string };
          if (msg && typeof msg.type === 'string') dispatch({ kind: 'message', msg, sym });
        } catch {
          // ignore malformed frame
        }
      };
      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat);
        heartbeat = null;
        if (cancelled) return;
        attempt += 1;
        const delay = Math.min(15_000, 500 * 2 ** Math.min(attempt, 5));
        dispatch({ kind: 'socket', status: 'reconnecting' });
        retry = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // onclose follows; nothing else to do.
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      if (heartbeat) clearInterval(heartbeat);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try {
          if (ws.readyState === WebSocket.OPEN)
            ws.send(JSON.stringify({ type: 'unsubscribe', symbol: sym }));
        } catch {
          // ignore
        }
        ws.close();
      }
    };
  }, [sym]);

  return state;
}
